import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  getPlayersList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  PlayerItem,
} from "store/players/list/slice";
import { playersListLimits } from "configs/limits";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import PlayersFilter from "./components/PlayersFilter";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { invalidateList } from "store/invalidateListAction";
import { useRefreshList } from "helpers/useRefreshList";
import ActionBtn from "components/buttons/ActionBtn";
import refreshIcon from "../../../assets/icons/refresh.svg";
import { RefreshButton } from "components/buttons/RefreshButton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";

const compactReference = (value: string, startLength = 6, endLength = 4) => {
  if (value.length <= startLength + endLength + 3) return value;

  return `${value.slice(0, startLength)}...${value.slice(-endLength)}`;
};

const truncateText = (value: string, maxLength: number) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const PlayersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activePlayerId = searchParams.get("entityId");

  const [order, setOrder] = useState<{
    id: string;
    email: string;
    country: string;
    currency: string;
    registeredAt: string;
    balanceReal: string;
    balanceBonus: string;
    category: string;
    riskStatus: string;
    verificationStatus: string;
  }>({
    id: "none",
    email: "none",
    country: "none",
    currency: "none",
    registeredAt: "none",
    balanceReal: "none",
    balanceBonus: "none",
    category: "none",
    riskStatus: "none",
    verificationStatus: "none",
  });
  const [firstDate, setFirstDate] = useState<string>("");
  const [secondDate, setSecondDate] = useState<string>("");
  const [currentLimit, setLimit] = useState<number>(playersListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [searchId, setSearchId] = useState<string>("");
  const [searchPublicId, setSearchPublicId] = useState<string>("");
  const [currencies, setCurrencies] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [risks, setRisks] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [roles, setRoles] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [geos, setGeos] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const [categories, setCategories] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [statuses, setStatuses] = useState<
    { value: string | number; label: string }[]
  >([]);

  const options = [
    {
      label: t("addToSegment"),
      value: "addToSegment",
    },
    {
      label: t("blockUser"),
      value: "blockUser",
    },
    {
      label: t("sendNotification"),
      value: "sendNotification",
    },
  ];

  const list = useAppSelector((store) => store.players.list);
  const isListFetched = useAppSelector((store) => store.players.isListFetched);
  const totalItems = useAppSelector((store) => store.players.totalItems);
  const totalPages = useAppSelector((store) => store.players.totalPages);
  const activePage = useAppSelector((store) => store.players.activePage);
  const processingItemId = useAppSelector(
    (store) => store.players.processingItemId,
  );
  const selectedItems = useAppSelector((store) => store.players.selectedItems);

  const publicIds = searchPublicId
    .split(/[\s,;]+/)
    .map((id) => id.trim())
    .filter(Boolean);
  const publicId =
    publicIds.length === 0
      ? undefined
      : publicIds.length === 1
        ? publicIds[0]
        : publicIds;
  const userIds = searchId
    .split(/[\s,;]+/)
    .map((id) => id.trim())
    .filter(Boolean)
    .map(Number)
    .filter((id) => Number.isInteger(id));

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({ sliceName: 'players', isListFetched });

  useEffect(() => {
    if (activePage !== 1) {
      dispatch(handlePaginate(1));
    }
    if (permissions && !permissions.read) {
      navigate("/dashboard");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getPlayersList({
          limit: currentLimit,
          offset: activePage - 1,
          email: search === "" ? undefined : search,
          userIds,
          publicId,
          categories: categories.map((el) => el.value as number),
          riskStatuses: risks.map((el) => el.value as number),
          roleIds: roles.map((el) => el.value as number),
          countries: geos.map((el) => el.value as string),
          currencies: currencies.map((el) => el.value as string),
          registrationFrom: firstDate,
          registrationTo: secondDate,
          verificationStatuses: statuses.map((elem) => elem.value as number),
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = (e: string) => {
    if (e === "addToSegment" && selectedItems.length > 0) {
      dispatch(
        openModal({
          activeModal: ModalTypes.addToSegment,
        }),
      );
    }
    if (e === "sendNotification" && selectedItems.length > 0) {
      dispatch(
        openModal({
          activeModal: ModalTypes.notificationSend,
        }),
      );
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.player }));
  };

  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };
  const handleFilterSearchId = (s: string) => {
    setSearchId(s);
  };
  const handleFilterSearchPublicId = (s: string) => {
    setSearchPublicId(s);
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleApplyDate = (firstDate: string, secondDate?: string) => {
    setFirstDate(firstDate);
    setSecondDate(secondDate ?? "");
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const listToRender = list.map(({ id, isOnline, email, ...rest }) => ({
    id,
    isOnline,
    email: email ?? "",
    ...rest,
    affId: undefined,
  }));

  

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PlayersFilter
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedCurrencies={currencies}
        handleUpdateCurrencies={(e) => setCurrencies(e)}
        selectedRisks={risks}
        handleUpdateRisks={(e) => setRisks(e)}
        selectedRoles={roles}
        handleUpdateRoles={(e) => setRoles(e)}
        selectedGeos={geos}
        handleUpdateGeos={(e) => setGeos(e)}
        selectedStatuses={statuses}
        handleUpdateStatuses={(e) => setStatuses(e)}
        selectedCategories={categories}
        handleUpdateCategories={(e) => setCategories(e)}
        handleApply={handleApply}
        firstDate={firstDate}
        secondDate={secondDate}
        handleApplyDate={handleApplyDate}
        searchId={searchId}
        handleSearchId={handleFilterSearchId}
        searchPublicId={searchPublicId}
        handleSearchPublicId={handleFilterSearchPublicId}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            {permissions?.update && (
              <DefaultDropdown
                className="default-dropdown--sentence-case"
                onSelect={(e) => {
                  handleActionClick(e as string);
                }}
                placeholder={t("chooseAction")}
                options={options}
                isSmall
                disabled={selectedItems.length === 0}
              />
            )}
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={13} rowsCount={15} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {listToRender.length === 0 && (
              <EmptyState message={t("noPlayersFound")} />
            )}

            {listToRender.length > 0 && (
              <Table
                data={
                  listToRender as Array<
                    PlayerItem & {
                      id: number;
                      notificationsCount: number;
                      emailConfirmed: boolean;
                    }
                  >
                }
                order={order}
                idField="id"
                selectedItems={selectedItems}
                onAllSelect={handleAllClick}
                onSelect={(id) => handleItemClick(id)}
                onEdit={(id) => handleOpenEditModal(id)}
                onView={(id) => handleOpenEditModal(id)}
                onEditByRowClick={(id) => handleOpenEditModal(id)}
                onSort={(id) => (id ? handleOrderClick(id) : {})}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasDelete={false}
                hasCheckbox={permissions?.update}
                activeRowId={activePlayerId}
                processingId={processingItemId}
                columns={{
                  fixedColumns: [
                    "id",
                    "isOnline",
                    "email",
                    "riskScore",
                    "country",
                    "currency",
                    "balanceReal",
                    "balanceBonus",
                    "verificationStatus",
                    "category",
                    "registeredAt",
                    "publicId",
                    "affiliateId",
                  ],
                  hiddenColumns: ["affId", "isTest"],
                }}
                customColumns={(row) => ({
                  email: (
                    <div className="player-email-cell">
                      <span>{row.email}</span>
                      {row.isTest && (
                        <span className="badge badge--test">test</span>
                      )}
                    </div>
                  ),
                  nickname: row.nickname ? (
                    <span title={row.nickname}>
                      {truncateText(row.nickname, 12)}
                    </span>
                  ) : (
                    <span>—</span>
                  ),
                  publicId: row.publicId ? (
                    <div className="copyable-title-cell">
                      <CopyToClipboard text={row.publicId} />
                      <span title={row.publicId}>
                        {compactReference(row.publicId)}
                      </span>
                    </div>
                  ) : (
                    <span>—</span>
                  ),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && listToRender.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={playersListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default PlayersList;
